/**
 * Console ring buffer — the last 50 console entries are attached to every
 * finding automatically.
 *
 * Accepted limit (documented in the plan): capture starts when the widget
 * loads, so anything logged before the script tag runs is not in the buffer.
 * Every finding carries `environment.consoleCaptureFrom` so a reader can tell.
 */

import type { ConsoleEntry } from "../shared/schemas";

const MAX_ENTRIES = 50;
const MAX_TEXT = 2_000;

const buffer: ConsoleEntry[] = [];
let captureFrom = "";
let installed = false;

function serialize(args: unknown[]): string {
  const parts = args.map((arg) => {
    if (typeof arg === "string") return arg;
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg); // circular / getter that throws
    }
  });
  const text = parts.join(" ");
  return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}…` : text;
}

function record(level: ConsoleEntry["level"], args: unknown[]): void {
  buffer.push({ level, time: new Date().toISOString(), text: serialize(args) });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

export function installConsoleCapture(): void {
  if (installed) return;
  installed = true;
  captureFrom = new Date().toISOString();

  for (const level of ["log", "warn", "error"] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      // Never let capture break the page: the original call always runs.
      try {
        record(level, args);
      } catch {
        /* ignore */
      }
      original(...args);
    };
  }

  window.addEventListener("error", (e) => {
    record("error", [
      `[window.onerror] ${e.message}`,
      `${e.filename}:${e.lineno}`,
    ]);
  });
  window.addEventListener("unhandledrejection", (e) => {
    record("error", ["[unhandledrejection]", e.reason]);
  });
}

export function getConsoleTail(): ConsoleEntry[] {
  return buffer.slice();
}

export function getCaptureFrom(): string {
  return captureFrom;
}
